import UsercentricsScriptEnabler from '@/components/usercentricsScriptEnabler/UsercentricsScriptEnabler';
import classNames from 'classnames';
import React from 'react';

import classes from './VideoPlayer.module.scss';

export type VideoPlayerProps = {
  url: string;
  poster?: string;
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;

  ariaLabel?: string;
  controlsList?: string; // NEW: allow passing controlsList to <video>
  iframeTitle?: string; // NEW: allow custom iframe title for accessibility
};

/**
 * Generic video player without Storyblok dependency.
 * - Supports YouTube URLs via iframe
 * - Supports direct video file URLs via <video>
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  poster,
  className,
  controls = true,
  autoPlay = false,
  muted = false,
  loop = false,

  ariaLabel = 'Video',
  controlsList,
  iframeTitle,
}) => {
  // Detect YouTube (short and full) and extract embed URL
  function getYoutubeEmbed(urlStr?: string | null) {
    const fullUrlPattern = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/;
    const shortUrlPattern = /https?:\/\/youtu\.be\/([^&]+)/;

    const fullMatch = urlStr?.match(fullUrlPattern) || [];
    const shortMatch = urlStr?.match(shortUrlPattern) || [];

    const videoId = fullMatch[1] || shortMatch[1];
    if (!videoId) return undefined;
    return `https://www.youtube.com/embed/${videoId}`;
  }

  const youtubeEmbed = getYoutubeEmbed(url);

  if (youtubeEmbed) {
    // If Usercentrics is available in project, keep it for privacy compliance
    return (
      <div className={classNames('video-player', classes.rounded, className)}>
        <UsercentricsScriptEnabler
          image={poster ? { filename: poster } : (undefined as any)}
          type={'youtube'}
          isFullScreen={false}
        >
          <div className={classNames(classes.rounded)}>
            <iframe
              className={classNames(classes.player, classes.iframe)}
              src={youtubeEmbed}
              title={iframeTitle || 'YouTube video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </UsercentricsScriptEnabler>
      </div>
    );
  }

  // HTML5 video fallback for direct URLs (mp4, webm, etc.)
  return (
    <div className={classNames('video-player', className)}>
      <video
        className={classNames(classes.player, classes.rounded)}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        poster={poster}
        title={ariaLabel}
        aria-label={ariaLabel}
        controlsList={controlsList}
      >
        <source src={url} />
      </video>
    </div>
  );
};

export default VideoPlayer;
